import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import './CadastroTiposMovimentacao.css'

/**
 * Tela simples para cadastrar Tipos de Movimentação (Entrada/Saída)
 * Usados no módulo Financeiro (ex: Venda, Contas, Serviços).
 */
const CadastroTiposMovimentacao = ({ onBack }) => {
  const [tipos, setTipos] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [nome, setNome] = useState('')
  const [natureza, setNatureza] = useState('entrada')
  const [editingId, setEditingId] = useState(null)
  const [editNome, setEditNome] = useState('')
  const [editNatureza, setEditNatureza] = useState('entrada')

  const loadTipos = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_entrada_saida')
        .select('id, nome, ativo, natureza')
        .order('nome')
      if (error) throw error
      setTipos(data || [])
    } catch (err) {
      console.error('Erro ao carregar tipos:', err)
      setMsg({ text: err.message || 'Erro ao carregar tipos.', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTipos()
  }, [loadTipos])

  const limparForm = useCallback(() => {
    setNome('')
    setNatureza('entrada')
    setMsg({ text: '', type: '' })
    setEditingId(null)
    setEditNome('')
    setEditNatureza('entrada')
  }, [])

  const handleCadastrar = async (e) => {
    e.preventDefault()
    const nomeTrim = nome.trim()
    if (!nomeTrim) {
      setMsg({ text: 'Informe o nome do tipo.', type: 'error' })
      return
    }
    setSaving(true)
    setMsg({ text: '', type: '' })
    try {
      const { error } = await supabase
        .from('tipo_entrada_saida')
        .insert([{ nome: nomeTrim, natureza: natureza || 'entrada', ativo: true }])
      if (error) throw error
      setMsg({ text: 'Tipo cadastrado com sucesso.', type: 'success' })
      setNome('')
      setNatureza('entrada')
      loadTipos()
      setTimeout(() => setMsg({ text: '', type: '' }), 3000)
    } catch (err) {
      if (err.code === '23505') {
        setMsg({ text: 'Já existe um tipo com este nome.', type: 'error' })
      } else {
        setMsg({ text: err.message || 'Erro ao cadastrar.', type: 'error' })
      }
    } finally {
      setSaving(false)
    }
  }

  const iniciarEdicao = (t) => {
    setEditingId(t.id)
    setEditNome(t.nome)
    setEditNatureza(t.natureza || 'entrada')
  }

  const cancelarEdicao = () => {
    setEditingId(null)
    setEditNome('')
    setEditNatureza('entrada')
  }

  const handleSalvarEdicao = async () => {
    const nomeTrim = editNome.trim()
    if (!nomeTrim) return
    setSaving(true)
    setMsg({ text: '', type: '' })
    try {
      const { error } = await supabase
        .from('tipo_entrada_saida')
        .update({ nome: nomeTrim, natureza: editNatureza || 'entrada' })
        .eq('id', editingId)
      if (error) throw error
      setMsg({ text: 'Tipo atualizado.', type: 'success' })
      cancelarEdicao()
      loadTipos()
      setTimeout(() => setMsg({ text: '', type: '' }), 3000)
    } catch (err) {
      if (err.code === '23505') {
        setMsg({ text: 'Já existe um tipo com este nome.', type: 'error' })
      } else {
        setMsg({ text: err.message || 'Erro ao atualizar.', type: 'error' })
      }
    } finally {
      setSaving(false)
    }
  }

  const toggleAtivo = async (t) => {
    try {
      const { error } = await supabase
        .from('tipo_entrada_saida')
        .update({ ativo: !t.ativo })
        .eq('id', t.id)
      if (error) throw error
      loadTipos()
    } catch (err) {
      setMsg({ text: err.message || 'Erro ao alterar status.', type: 'error' })
    }
  }

  return (
    <div className="cadastro-tipos">
      <div className="cadastro-tipos-header">
        <button type="button" className="cadastro-tipos-btn-back" onClick={onBack} aria-label="Voltar">
          ← Voltar
        </button>
        <h1 className="cadastro-tipos-title">Cadastro de Tipos de Movimentação</h1>
      </div>

      <p className="cadastro-tipos-descricao">
        Cadastre os tipos usados nas movimentações de Entrada/Saída (ex: Venda, Contas, Serviços).
      </p>

      {/* Card Cadastrar */}
      <section className="cadastro-tipos-card">
        <h2 className="cadastro-tipos-card-titulo">
          <span className="cadastro-tipos-card-icone">➕</span>
          Novo tipo
        </h2>
        <form onSubmit={handleCadastrar} className="cadastro-tipos-form">
          <label className="cadastro-tipos-label">
            Nome do tipo
            <input
              type="text"
              className="cadastro-tipos-input"
              placeholder="Ex: Venda, Contas, Serviços"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={100}
            />
          </label>
          <label className="cadastro-tipos-label">
            Entrada ou Saída
            <select
              className="cadastro-tipos-input"
              value={natureza}
              onChange={(e) => setNatureza(e.target.value)}
            >
              <option value="entrada">Entrada</option>
              <option value="saida">Saída</option>
            </select>
          </label>
          <button type="submit" className="cadastro-tipos-btn-cadastrar" disabled={saving}>
            {saving ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </form>
      </section>

      {msg.text && (
        <div className={`cadastro-tipos-msg cadastro-tipos-msg-${msg.type}`} role="alert">
          {msg.text}
        </div>
      )}

      {/* Listagem */}
      <section className="cadastro-tipos-lista">
        <h2 className="cadastro-tipos-lista-titulo">Tipos cadastrados</h2>
        {loading ? (
          <p className="cadastro-tipos-loading">Carregando...</p>
        ) : (
          <div className="cadastro-tipos-tabela-wrap">
            <table className="cadastro-tipos-tabela">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th className="cadastro-tipos-th-natureza">Entrada/Saída</th>
                  <th className="cadastro-tipos-th-status">Ativo</th>
                  <th className="cadastro-tipos-th-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {tipos.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="cadastro-tipos-empty">
                      Nenhum tipo cadastrado. Use o formulário acima para cadastrar.
                    </td>
                  </tr>
                ) : (
                  tipos.map((t) => (
                    <tr key={t.id} className={!t.ativo ? 'cadastro-tipos-row-inativo' : ''}>
                      <td>
                        {editingId === t.id ? (
                          <div className="cadastro-tipos-edit-inline">
                            <input
                              type="text"
                              className="cadastro-tipos-input cadastro-tipos-input-inline"
                              value={editNome}
                              onChange={(e) => setEditNome(e.target.value)}
                              maxLength={100}
                              autoFocus
                            />
                            <button
                              type="button"
                              className="cadastro-tipos-btn-salvar-inline"
                              onClick={handleSalvarEdicao}
                              disabled={saving || !editNome.trim()}
                            >
                              Salvar
                            </button>
                            <button type="button" className="cadastro-tipos-btn-cancelar-inline" onClick={cancelarEdicao}>
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          t.nome
                        )}
                      </td>
                      <td className="cadastro-tipos-td-natureza">
                        {editingId === t.id ? (
                          <select
                            className="cadastro-tipos-input cadastro-tipos-select-inline"
                            value={editNatureza}
                            onChange={(e) => setEditNatureza(e.target.value)}
                          >
                            <option value="entrada">Entrada</option>
                            <option value="saida">Saída</option>
                          </select>
                        ) : (
                          <span className={`cadastro-tipos-badge natureza-${t.natureza || 'entrada'}`}>
                            {t.natureza === 'saida' ? 'Saída' : 'Entrada'}
                          </span>
                        )}
                      </td>
                      <td className="cadastro-tipos-td-status">
                        <span className={`cadastro-tipos-badge ${t.ativo ? 'ativo' : 'inativo'}`}>
                          {t.ativo ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className="cadastro-tipos-td-acoes">
                        {editingId === t.id ? null : (
                          <>
                            <button
                              type="button"
                              className="cadastro-tipos-btn-acao"
                              onClick={() => iniciarEdicao(t)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="cadastro-tipos-btn-acao"
                              onClick={() => toggleAtivo(t)}
                            >
                              {t.ativo ? 'Inativar' : 'Ativar'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default CadastroTiposMovimentacao
